import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

export const ClaimForm = () => {
  const ref = useReveal();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    state: "",
    county: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.address) {
      toast.error("Please complete name, email, phone, and address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setSuccess(true);
      toast.success("Claim check submitted. We will reach out within 1 business day.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        address: "",
        state: "",
        county: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call 817-680-2751.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="claim"
      data-testid="claim-section"
      ref={ref}
      className="bg-[#F3F0E6] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-[#1E3A5F] text-xs uppercase tracking-[0.3em]">
                Free Claim Check
              </span>
            </div>
            <h2 className="font-serif-display text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05]">
              Let's see if
              <br />
              <em className="italic text-[#1E3A5F]">funds are waiting.</em>
            </h2>
            <p className="mt-8 text-[#1E3A5F] text-lg leading-relaxed max-w-md">
              Share a few details and our research team will quietly check the
              relevant county and court registries. There is no obligation, no
              fee, and no commitment.
            </p>

            <div className="mt-12 space-y-4 max-w-md">
              <Trust line="100% confidential — we never sell or share your information." />
              <Trust line="Zero upfront cost. We're only paid if we recover funds." />
              <Trust line="Direct line: 817-680-2751 — speak with a real person." />
            </div>
          </div>

          <div className="lg:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
            <div className="bg-[#FDFBF7] border border-[#E2DCD0] p-8 md:p-12 rounded-sm">
              {success ? (
                <div className="text-center py-16" data-testid="claim-success">
                  <CheckCircle2 className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
                  <h3 className="font-serif-display text-3xl text-[#0A192F] mb-3">
                    Submission received
                  </h3>
                  <p className="text-[#1E3A5F] max-w-md mx-auto">
                    A member of our recovery team will contact you within
                    1 business day. If your matter is urgent, call us at{" "}
                    <a
                      href="tel:8176802751"
                      className="text-[#D4AF37] font-semibold"
                    >
                      817-680-2751
                    </a>
                    .
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    data-testid="submit-another"
                    className="mt-8 text-sm underline text-[#1E3A5F] hover:text-[#0A192F]"
                  >
                    Submit another claim check
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="claim-form"
                  className="space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Full Name"
                      id="full_name"
                      value={form.full_name}
                      onChange={update("full_name")}
                      placeholder="John A. Doe"
                      testId="input-full-name"
                      required
                    />
                    <Field
                      label="Email"
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="john@example.com"
                      testId="input-email"
                      required
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Phone"
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(555) 123-4567"
                      testId="input-phone"
                      required
                    />
                    <Field
                      label="Property Address"
                      id="address"
                      value={form.address}
                      onChange={update("address")}
                      placeholder="123 Main St, Houston, TX"
                      testId="input-address"
                      required
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="State"
                      id="state"
                      value={form.state}
                      onChange={update("state")}
                      placeholder="Texas"
                      testId="input-state"
                    />
                    <Field
                      label="County"
                      id="county"
                      value={form.county}
                      onChange={update("county")}
                      placeholder="Harris"
                      testId="input-county"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="message"
                      className="text-xs uppercase tracking-[0.18em] text-[#1E3A5F] font-medium"
                    >
                      Anything we should know? (Optional)
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Foreclosure date, deceased relative, etc."
                      rows={4}
                      className="mt-2 bg-[#FDFBF7] border-[#E2DCD0] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 rounded-sm"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="claim-submit-btn"
                    disabled={loading}
                    className="w-full bg-[#0A192F] hover:bg-[#112B4F] text-[#FDFBF7] rounded-sm py-6 text-base font-medium tracking-wide transition-colors"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting…
                      </span>
                    ) : (
                      "Start My Free Claim Check"
                    )}
                  </Button>

                  <p className="text-xs text-[#1E3A5F] text-center leading-relaxed">
                    By submitting, you agree to be contacted regarding your
                    potential surplus claim. We never share your data.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, id, type = "text", value, onChange, placeholder, testId, required }) => (
  <div>
    <Label
      htmlFor={id}
      className="text-xs uppercase tracking-[0.18em] text-[#1E3A5F] font-medium"
    >
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </Label>
    <Input
      id={id}
      type={type}
      data-testid={testId}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="mt-2 bg-[#FDFBF7] border-[#E2DCD0] focus-visible:ring-[#D4AF37] focus-visible:ring-offset-0 rounded-sm h-12"
    />
  </div>
);

const Trust = ({ line }) => (
  <div className="flex items-start gap-3">
    <ShieldCheck className="h-5 w-5 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
    <p className="text-[#1E3A5F] text-sm">{line}</p>
  </div>
);
